// SVG-Path-Generatoren pro Shape-Kind. Pure Funktionen, vitest-tauglich.
// Path-Spec relative zum Shape-Top-Left (0,0) bis (width, height).

import type { ShapeKind, ShapeSemantics } from "./objects/types.js";

export function shapePath(kind: ShapeKind, width: number, height: number): string {
  const w = width;
  const h = height;
  switch (kind) {
    case "ellipse": {
      const rx = w / 2;
      const ry = h / 2;
      return `M ${rx} 0 A ${rx} ${ry} 0 1 0 ${rx} ${h} A ${rx} ${ry} 0 1 0 ${rx} 0 Z`;
    }
    case "rounded-rect": {
      const r = Math.min(12, w / 2, h / 2);
      return `M ${r} 0 L ${w - r} 0 Q ${w} 0 ${w} ${r} L ${w} ${h - r} Q ${w} ${h} ${w - r} ${h} L ${r} ${h} Q 0 ${h} 0 ${h - r} L 0 ${r} Q 0 0 ${r} 0 Z`;
    }
    case "diamond":
      return `M ${w / 2} 0 L ${w} ${h / 2} L ${w / 2} ${h} L 0 ${h / 2} Z`;
    case "hexagon": {
      // Horizontal-orientierter Hexagon: Spitze links + rechts
      const off = Math.min(20, w / 4);
      return `M ${off} 0 L ${w - off} 0 L ${w} ${h / 2} L ${w - off} ${h} L ${off} ${h} L 0 ${h / 2} Z`;
    }
    case "parallelogram": {
      const skew = Math.min(20, w / 5);
      return `M ${skew} 0 L ${w} 0 L ${w - skew} ${h} L 0 ${h} Z`;
    }
    case "circle": {
      // Immer rund: zentrierter Kreis. Radius um 3px eingerueckt, damit die Strichbreite
      // (bis 2.6px bei bpmn:end) nicht an der Box-/viewBox-Kante abgeschnitten wird.
      const r = Math.max(2, Math.min(w, h) / 2 - 3);
      const cx = w / 2;
      const cy = h / 2;
      return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 Z`;
    }
  }
}

// --- BPMN-Semantics (Welle 3) ---
// Voller BPMN-Picker: jede Semantik hat einen Default-Kind, eine Default-Accent-Farbe,
// eine Strichstaerke und ggf. Overlay-Marker (z.B. Gateway-X).

export const BPMN_SEMANTICS: readonly ShapeSemantics[] = [
  "bpmn:start",
  "bpmn:end",
  "bpmn:task",
  "bpmn:gateway",
  "bpmn:subprocess",
  "bpmn:data",
];

export const BPMN_DEFAULT_KIND: Record<ShapeSemantics, ShapeKind> = {
  "bpmn:start": "circle",
  "bpmn:end": "circle",
  "bpmn:task": "rounded-rect",
  "bpmn:gateway": "diamond",
  "bpmn:subprocess": "rounded-rect",
  "bpmn:data": "parallelogram",
};

export const BPMN_DEFAULT_ACCENT: Record<ShapeSemantics, string> = {
  "bpmn:start": "#1f7a1f",
  "bpmn:end": "#c83232",
  "bpmn:task": "#4a8caf",
  "bpmn:gateway": "#fdc300",
  "bpmn:subprocess": "#4a8caf",
  "bpmn:data": "#999999",
};

export const BPMN_GLYPH: Record<ShapeSemantics, string> = {
  "bpmn:start": "○",
  "bpmn:end": "◉",
  "bpmn:task": "▭",
  "bpmn:gateway": "◇",
  "bpmn:subprocess": "▣",
  "bpmn:data": "▱",
};

export function bpmnStrokeWidth(semantics: ShapeSemantics): number {
  if (semantics === "bpmn:end") return 2.6;
  if (semantics === "bpmn:start") return 1.0;
  return 1.4;
}

/** Overlay-Marker (zusaetzlich zum Haupt-Pfad). Nur Stroke, kein Fill. */
export function bpmnMarkerPaths(
  semantics: ShapeSemantics,
  width: number,
  height: number,
): string[] {
  const w = width;
  const h = height;
  if (semantics === "bpmn:gateway") {
    const cx = w / 2;
    const cy = h / 2;
    const r = Math.min(w, h) * 0.2;
    return [
      `M ${cx - r} ${cy - r} L ${cx + r} ${cy + r}`,
      `M ${cx + r} ${cy - r} L ${cx - r} ${cy + r}`,
    ];
  }
  if (semantics === "bpmn:subprocess") {
    const bx = w / 2 - 6;
    const by = h - 16;
    return [
      `M ${bx} ${by} h 12 v 12 h -12 Z`,
      `M ${bx + 3} ${by + 6} h 6`,
      `M ${bx + 6} ${by + 3} v 6`,
    ];
  }
  return [];
}
